import { EventNote as CalendarIcon } from "@mui/icons-material";
import type { SxProps, Theme } from "@mui/material";
import { FormHelperText, InputAdornment } from "@mui/material";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTime } from "luxon";
import { toDateFormat } from "../../../helpers/date-helpers";
import type { ChangeHandler, DatePicker as TDatePicker } from "../types/Modify";
import { PickerChild } from "./PickerChild";

interface Props<D extends object> {
  config: Omit<TDatePicker<D>, "controltype">;
  handleChange: ChangeHandler;
}

const borderStyles: SxProps<Theme> = {
  borderRadius: 0,
};

export const DatePicker = <D extends object>({
  config,
  handleChange,
}: Props<D>) => {
  const {
    name,
    label,
    required,
    disabled,
    value,
    error,
    helperText,
    outputFormatter = toDateFormat,
  } = config;
  const dateValue =
    typeof value === "string" && value ? DateTime.fromISO(value) : null;

  return (
    <>
      <MuiDatePicker
        renderInput={(props) => (
          <PickerChild
            name={name}
            {...props}
            required={required}
            error={error}
          />
        )}
        value={dateValue}
        disabled={disabled}
        InputProps={{
          title: name,
          id: name,
          sx: [borderStyles],
          endAdornment: (
            <InputAdornment position="end">
              <CalendarIcon />
            </InputAdornment>
          ),
        }}
        onChange={(date) => {
          if (!date) return;
          const dateString = outputFormatter(date);
          handleChange({
            target: {
              name,
              value: dateString,
            },
          });
        }}
        label={label}
      />
      <FormHelperText error={error} children={helperText} />
    </>
  );
};
